import * as React from "react"
import { useRecoilValue, useSetRecoilState } from "recoil"
import { pollsAtom } from "../../../recoil/atoms/polls"
import { ChatPoll } from "./chat-poll"
import { Box } from "@chakra-ui/react"
import styled from "@emotion/styled"
import type { RoomBranding } from "../../../recoil/atoms/room/types"
import { brandingSelector } from "../../../recoil/atoms/room"
import layoutStates from "../../../recoil/atoms/layout"
import { chatIsReadAtom } from "../../../recoil/atoms/chats"

interface ChatPollsProps {
  isMobile?: boolean
  isFullScreen?: boolean
  isLandscape?: boolean
  isTransparent?: boolean
}

interface StyledProps {
  roomStyles?: RoomBranding
}

const Container = styled(Box)<StyledProps>`
  &::-webkit-scrollbar-track {
    background: ${({ roomStyles }) =>
      roomStyles.servicePanelElementBorder || "#cbd5e0"};
    border-radius: 10px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${({ roomStyles }) =>
      roomStyles.servicePanelElementBorderOffset || "#fff"};
    border-radius: 10px;
  }
`

export const ChatPolls: React.FC<ChatPollsProps> = React.memo(
  ({ isMobile, isFullScreen, isLandscape, isTransparent }) => {
    const polls = useRecoilValue(pollsAtom)
    const roomStyles = useRecoilValue(brandingSelector)
    const mobilePoll = polls
      .filter(el => el.isVisible && el.isVisibleMobile)
      .sort((a, b) => (a?.timestamp < b?.timestamp ? -1 : 1))

    const isVisiblePolls = React.useMemo(
      () => polls.some(poll => poll.isVisible),
      [polls]
    )

    const isChatTab = useRecoilValue<boolean>(layoutStates.isChatTab)
    const setChatIsRead = useSetRecoilState(chatIsReadAtom)
    const previousSelectedPollLenghtRef = React.useRef(0)
    const selectedPolls = React.useMemo(
      () => polls.filter(el => el.isVisible === true),
      [polls]
    )

    React.useEffect(() => {
      if (
        !isChatTab &&
        selectedPolls.length > previousSelectedPollLenghtRef.current &&
        !isMobile
      ) {
        setChatIsRead(false)
      }
      previousSelectedPollLenghtRef.current = selectedPolls.length
    }, [selectedPolls.length, isChatTab, setChatIsRead, isMobile])

    return isMobile ? (
      <>
        {mobilePoll.length !== 0 ? (
          <Box
            id="chat-polls"
            key={`${mobilePoll[mobilePoll.length - 1].id}-container`}
            width={isFullScreen ? "84vw" : isLandscape ? "350px" : "100%"}
            marginBottom={isTransparent && "10px"}
          >
            <Box
              padding={
                isTransparent
                  ? "0px"
                  : isFullScreen
                  ? "0rem 0rem 1rem 0rem"
                  : "0.5rem 8px 0.5rem 8px"
              }
            >
              <ChatPoll
                poll={mobilePoll[mobilePoll.length - 1]}
                isFullScreen={isFullScreen}
                isLandscape={isLandscape}
                isMobile={true}
              />
            </Box>
          </Box>
        ) : null}
      </>
    ) : (
      <Container
        maxH="50%"
        overflowX="hidden"
        overflowY="auto"
        marginTop={isVisiblePolls && "1rem"}
        roomStyles={roomStyles}
        scrollbarwidth="thin"
        style={{ scrollBehavior: "smooth" }}
        padding="0rem 0.5rem 0rem 1rem"
        marginRight="0.5rem"
      >
        {polls.map((poll, index) => {
          if (poll.isVisible) {
            return (
              <Box
                key={`key-${poll?.id}`}
                paddingTop="1rem"
                _first={{ paddingTop: "0rem" }}
              >
                <ChatPoll poll={poll} />
              </Box>
            )
          }
          return null
        })}
      </Container>
    )
  }
)
